import React, { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { headerPropTypes } from './HeaderPropTypes';
import { BREAKPOINTS } from '@Theme/natucare/constants/breakpoints';
import { DEVICE_TYPES } from '@Theme/natucare/constants/deviceTypes';
import ReadingIndicator from './components/ReadingIndicator/ReadingIndicator';
import PromoBar from './components/PromoBar/PromoBar';
import MainBar from './components/MainBar/MainBar';
import useScrollDirection from '@App/common/hooks/useScrollDirection';
import { useDeviceDetect } from '@App/common/hooks/useDeviceDetect';
import { useDocumentClass } from '@App/common/hooks/useDocumentClass';
import * as styles from './Header.module.scss';
const Header = /*#__PURE__*/forwardRef(({
  promoBar,
  mainBar,
  onChange,
  searchResults,
  searchTerm,
  sendSearchData,
  isBlogPostTemplate
}, ref) => {
  if (!promoBar && !mainBar) {
    return null;
  }
  const {
    deviceType
  } = useDeviceDetect(BREAKPOINTS);
  const isDesktop = deviceType !== DEVICE_TYPES[BREAKPOINTS.XS] && deviceType !== DEVICE_TYPES[BREAKPOINTS.SM] && deviceType !== DEVICE_TYPES[BREAKPOINTS.MD];
  const headerRef = useRef(null);
  const {
    0: isMobileOpen,
    1: setIsMobileOpen
  } = useState(false);
  const {
    0: isSearching,
    1: setIsSearching
  } = useState(false);
  const {
    0: hasScrolled,
    1: setHasScrolled
  } = useState(false);
  const handleOpenMobile = useCallback(() => {
    if (isDesktop) {
      return;
    }
    setIsSearching(false);
    setIsMobileOpen(prev => !prev);
  }, [deviceType]);
  const shouldHideOverflow = isDesktop && isSearching || !isDesktop && (isMobileOpen || isSearching);
  useDocumentClass(styles.isOverflowHidden, shouldHideOverflow);
  const {
    isVisible
  } = useScrollDirection(100, 0);
  const mainBarRef = useRef(null);
  const {
    0: mainBarOffset,
    1: setMainBarOffset
  } = useState(0);
  useEffect(() => {
    if ((isMobileOpen || isSearching) && mainBarRef.current) {
      const rect = mainBarRef.current.getBoundingClientRect();
      setMainBarOffset(rect.top);
    }
  }, [isMobileOpen, isSearching]);
  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }
    const handleScroll = () => {
      setHasScrolled(window.scrollY > 0);
    };
    handleScroll();
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const inlineCssVariableMainBarTopOffset = {
    '--main-bar-top-offset': `${mainBarOffset}px`
  };
  const handleBackgroundClickWhileSearch = e => {
    if (headerRef.current === e.target) {
      setIsSearching(false);
    }
  };

  /**
   * Prop objects with data, functions and states
   */
  const mainBarProps = {
    ...mainBar,
    onChange,
    isVisible,
    isMobileOpen,
    handleOpenMobile,
    setIsSearching,
    isSearching,
    searchResults,
    searchTerm,
    sendSearchData,
    hasScrolled
  };
  const headerClassnames = `
  ${styles.root} 
  ${isSearching ? styles.rootSearching : ''}  
  ${!isVisible && isBlogPostTemplate ? styles.rootScrollHidden : ''}`;
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(ReadingIndicator, {
    ref: ref
  }), /*#__PURE__*/React.createElement("header", {
    ref: headerRef,
    onClick: handleBackgroundClickWhileSearch,
    style: inlineCssVariableMainBarTopOffset,
    className: headerClassnames
  }, /*#__PURE__*/React.createElement(PromoBar, promoBar), /*#__PURE__*/React.createElement(MainBar, Object.assign({}, mainBarProps, {
    headerRef: headerRef,
    ref: mainBarRef
  }))));
});
Header.displayName = 'Header';
export default Header;